import React from 'react';
import { Button, Form, Input } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { DeleteFilled } from '@ant-design/icons';
import CustomModal from '../../../components/common/CustomModal';
import { SortableTableHeader, DataTable } from '../../../components/common';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getBonus, removeBonus, addBonus } from '../../../store/slices/adminSlice';
import { useTableState, useModal, useDeleteConfirm, useDataLoading } from '../../../hooks';
import { requiredRule } from '../../../utils';

// TypeScript interfaces
interface BonusData {
  _id: string;
  bonusname: string;
  description: string;
}

interface BonusFormValues {
  bonusname: string;
  description: string;
}

const Bonus: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<BonusFormValues>();

  // Redux state
  const bonusData = useAppSelector((state) => state.admin.bonusData) as BonusData[];
  const totalCount = useAppSelector((state) => state.admin.bonusCount);

  // Use custom hooks
  const { currentPage, sortOrder, changePage, sortData } = useTableState({
    fetchAction: getBonus,
  });

  const { visible, showModal, hideModal } = useModal({ form });

  const { showDeleteConfirm } = useDeleteConfirm({
    deleteAction: removeBonus,
    idParamName: 'bonusId',
  });

  const [loading] = useDataLoading(bonusData);

  const handleShowModal = (id: string, name: string, description: string) => {
    showModal(id);
    form.setFieldsValue({ bonusname: name, description: description });
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const obj = {
        bonusname: values.bonusname,
        description: values.description,
      };
      hideModal();
      dispatch(addBonus(obj));
    } catch (error) {
      // Validation failed - form will show errors
    }
  };

  // Define table columns
  const columns = [
    {
      key: 'bonusname',
      title: (
        <SortableTableHeader
          label="Bonus Name"
          sortKey="bonusname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: BonusData) => item.bonusname,
    },
    {
      key: 'description',
      title: (
        <SortableTableHeader
          label="Description"
          sortKey="description"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: BonusData) => item.description,
    },
    {
      key: 'actions',
      title: 'Action',
      className: 'action-icons',
      render: (item: BonusData) => (
        <Button
          type="link"
          className="btn-sm mr-2"
          onClick={() => showDeleteConfirm(item._id)}
        >
          <DeleteFilled style={{ color: '#666' }} />
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader
          prefixedClassName="ant-page-header"
          title="BONUS"
          subTitle=""
          extra={[
            <Button
              key="1"
              type="primary"
              onClick={() => handleShowModal('', '', '')}
            >
              Add Bonus
            </Button>,
          ]}
        />
        <DataTable
          columns={columns}
          data={bonusData}
          loading={loading}
          currentPage={currentPage}
          pageSize={5}
          total={totalCount}
          onPageChange={changePage}
          sortOrder={sortOrder}
          onSort={sortData}
          rowKey="_id"
        />
        <CustomModal
          handleCancel={hideModal}
          handleOk={handleOk}
          visible={visible}
          title="Bonus"
          backBtnText="Back"
          submitBtnText="Submit"
        >
          <Form form={form} className="bonus-form" layout="vertical">
            <Form.Item
              label="Bonus Name"
              name="bonusname"
              rules={[requiredRule('bonus name')]}
            >
              <Input placeholder="Bonus" />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              rules={[
                {
                  required: false,
                  whitespace: true,
                  message: 'Please enter description',
                },
              ]}
            >
              <Input placeholder="Description" />
            </Form.Item>
          </Form>
        </CustomModal>
      </div>
    </>
  );
};

export default Bonus;
